// Layout-level React components (sidebar, topbar, modal, toast)

import React, { useState, useEffect, useRef } from "react";
import { AppState, RouteMeta, uniq, todayStr, nowStr, Ticket } from './state';

interface SidebarProps {
  state: AppState;
  setRoute: (route: string) => void;
  toggleRole: () => void;
}

  const storedUser = localStorage.getItem("user");
  const portalUser = storedUser ? JSON.parse(storedUser) : null;
  const isInternalAdmin =
  portalUser?.system_role === "INTERNAL_SUPER_ADMIN";

export function Sidebar({ state, setRoute, toggleRole }: SidebarProps): React.ReactElement {
 const routes = [
  { id: "dashboard", label: "Dashboard", icon: "DB" },
  { id: "products", label: "Products", icon: "PR" },
  { id: "downloads", label: "Downloads", icon: "DL" },
  { id: "repos", label: "Repos", icon: "RP" },
  { id: "docs", label: "Documentation", icon: "KB" },
  { id: "support", label: "Support", icon: "SP" },
  { id: "account", label: "Account", icon: "AC" },
];

if (portalUser?.system_role === "INTERNAL_SUPER_ADMIN") {
  routes.push({
    id: "admin",
    label: "Admin",
    icon: "AD",
  });
}
  return (
    <aside className="sidebar">
      <div className="brand" role="banner">
        <div className="logo" aria-hidden="true" />
        <div>
          <h1>CCRT Customer Portal</h1>
          <p>Unified access for RedHawk + SimWB</p>
        </div>
      </div>

      <nav className="nav" aria-label="Primary">
        {routes.map(r => (
          <button
            key={r.id}
            className={state.route === r.id ? "active" : ""}
            onClick={() => setRoute(r.id)}
            data-route={r.id}
          >
            <span className="icon">{r.icon}</span>
            {r.label}
          </button>
        ))}
      </nav>

      <div className="sidebarFooter">
        <div className="pillRow">
          <span className="pill"><strong>Org:</strong> CCRT</span>
          <span className="pill"><strong>Tier:</strong> Maintenance</span>
        </div>
        <div className="pillRow">
          <span className="pill"><strong>Products:</strong> RedHawk, SimWB</span>
        </div>
      </div>
    </aside>
  );
}

interface TopbarProps {
  state: AppState;
  meta: RouteMeta;
  searchResults: {
  id: string;
  title: string;
  subtitle: string;
  route: string;
}[];
  onGlobalSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setRoute: (route: string) => void;
  openTicketModal: () => void;
  
}

interface PortalUser {
  id: number;
  name: string;
  email: string;
  login: string;
}

function getPortalUser(): PortalUser | null {
  const storedUser = localStorage.getItem("user");

  if (!storedUser) return null;

  try {
    return JSON.parse(storedUser) as PortalUser;
  } catch {
    return null;
  }
}

export function Topbar({
  state,
  meta,
  onGlobalSearchChange,
  setRoute,
  searchResults,
  openTicketModal,
}: TopbarProps): React.ReactElement {
  const portalUser = getPortalUser();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  // This is for the search bar dropdown
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const searchRef = useRef<HTMLDivElement>(null);

useEffect(() => {
  const handleClickOutside = (event: MouseEvent) => {
    if (
      searchRef.current &&
      !searchRef.current.contains(event.target as Node)
    ) {
      setIsSearchOpen(false);
    }
  };

  document.addEventListener("mousedown", handleClickOutside);

  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, []);

const searchQuery = state.globalQuery.trim().toLowerCase();

// const searchSections = [
//   { label: "Products", route: "products", keywords: "products redhawk simulation workbench" },
//   { label: "Downloads", route: "downloads", keywords: "downloads iso software versions releases" },
//   { label: "Repositories", route: "repos", keywords: "repos repositories packages" },
//   { label: "Documentation", route: "docs", keywords: "docs documentation manuals guides knowledge base" },
//   { label: "Support Tickets", route: "support", keywords: "support tickets issues requests" },
// ];



const handleSearchNavigation = (route: string) => {
  setRoute(route);
  setIsSearchOpen(false);
};

// This is for logout
const handleLogout = async () => {
  try {
    const logoutUrl =
      process.env.NODE_ENV === "production"
        ? "/api/auth/logout"
        : "http://localhost:4000/api/auth/logout";

    const response = await fetch(logoutUrl, {
      method: "POST",
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to invalidate session");
    }

    // Clear frontend user data
    localStorage.removeItem("user");
    localStorage.removeItem("legacyUser");

    // Redirect and reload the application
    window.location.replace("/");
  } catch (error) {
    console.error("Logout error:", error);
    alert("Unable to log out. Please try again.");
  }
};

  return (
    <header className="topbar">
      <div className="leftTop">
        <div className="pageTitle">
          <h2>{meta.title}</h2>
          <p>{meta.subtitle}</p>
        </div>

        <div
  className="search"
  ref={searchRef}
  title="Global search"
  style={{ position: "relative" }}
>
  <span className="icon" aria-hidden="true">
    ⌕
  </span>

  <input
    placeholder="Search docs, downloads, tickets…"
    aria-label="Global search"
    value={state.globalQuery}
    onChange={(e) => {
      onGlobalSearchChange(e);
      setIsSearchOpen(true);
    }}
    onFocus={() => setIsSearchOpen(true)}
    onKeyDown={(e) => {
      if (e.key === "Escape") {
        setIsSearchOpen(false);
      }
    }}
  />

  <span className="kbd">Ctrl K</span>

  {isSearchOpen && searchQuery && (
    <div
      className="globalSearchDropdown"
      style={{
        position: "absolute",
        top: "100%",
        left: 0,
        width: "100%",
        background: "#fff",
        color: "#222",
        border: "1px solid #ddd",
        borderRadius: 8,
        boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
        zIndex: 1000,
        maxHeight: 320,
        overflowY: "auto",
      }}
    >
     {searchResults.length > 0 ? (
  searchResults.map((result) => (
    <button
      key={result.id}
      type="button"
      onClick={() => handleSearchNavigation(result.route)}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        width: "100%",
        padding: "12px 14px",
        textAlign: "left",
        background: "transparent",
        border: "none",
        borderBottom: "1px solid #eee",
        cursor: "pointer",
        color: "inherit",
      }}
    >
      <strong>{result.title}</strong>
      <span style={{ fontSize: 12, color: "#666" }}>
        {result.subtitle}
      </span>
    </button>
  ))
) : (
  <div style={{ padding: 14 }}>
    No matching results found.
  </div>
)}
    </div>
  )}
</div>
      </div>

      <div className="rightTop">
        <button
          className="btn primary"
          onClick={meta.primaryOnClick || openTicketModal}
        >
          {meta.primaryLabel}
        </button>

        <button
          className="btn"
          onClick={() =>
            window.alert("Mock: Help opens a KB search or contact info.")
          }
        >
          Help
        </button>

        <div className="userMenuWrapper">
          <button
            type="button"
            className="userChip userChipButton"
            onClick={() => setIsUserMenuOpen((prev) => !prev)}
          >
            <div className="avatar" aria-hidden="true" />
            <div className="meta">
              <div className="name">{portalUser?.name || "Portal User"}</div>
              <div className="role">{state.role}</div>
            </div>
            <span style={{ fontSize: 12, opacity: 0.7 }}>
              {isUserMenuOpen ? "▲" : "▼"}
            </span>
          </button>

          {isUserMenuOpen && (
            <div className="userDropdown">
              <div className="userDropdownHeader">
                <strong>{portalUser?.name || "Portal User"}</strong>
                <span>{portalUser?.email || "No email found"}</span>
              </div>

              <button
                type="button"
                className="userDropdownItem"
                onClick={() => {
                  setIsUserMenuOpen(false);
                  window.alert("Profile page coming soon.");
                }}
              >
                Profile
              </button>

              <button
                type="button"
                className="userDropdownItem danger"
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

interface TicketModalProps {
  state: AppState;
  setState: React.Dispatch<React.SetStateAction<AppState>>;
  close: () => void;
}

export function TicketModal({ state, setState, close }: TicketModalProps): React.ReactElement {
  const [product, setProduct] = useState<string>("RedHawk Linux");
  const [version, setVersion] = useState<string>("");
  const [severity, setSeverity] = useState<string>("Low");
  const [env, setEnv] = useState<string>("");
  const [desc, setDesc] = useState<string>("");

  const versions = uniq(
    state.releases
      .filter(r => r.product === product)
      .map(r => r.version)
  );

  useEffect(() => {
    setVersion(versions[0] || "—");
  }, [product]);

  const isSupportExpired = (p: string): boolean => {
    const m = state.org.maintenance[p];
    return m !== undefined && m.status === "Expired";
  };

  const expired = isSupportExpired(product);

  const submit = (): void => {
    if (!desc.trim()) {
      window.alert("Add a description before submitting.");
      return;
    }
    const id = `CCRT-${Math.floor(2600 + Math.random()*200)}`;
    const subject = desc.split("\n")[0].slice(0, 70) || "New ticket";
    const t: Ticket = {
      id,
      product,
      version,
      status: "Open",
      severity: severity as "Low" | "Medium" | "High" | "Critical",
      created: todayStr(),
      updated: todayStr(),
      subject,
      owner: "Support",
      messages: [
        { by:"Steve", when: nowStr(), text: `${desc}\n\nEnvironment: ${env || "—"}` },
        ...(expired ? [{ by:"Support", when: nowStr(), text:"Note: Coverage is expired; this ticket may be handled per policy. (Mock)" }] : [])
      ]
    };
    setState(prev => ({
      ...prev,
      tickets: [t, ...prev.tickets],
      selectedTicketId: id,
    }));
    window.alert(`Ticket submitted: ${id} (mock).`);
    close();
  };

  return (
    <div className="modalOverlay" role="dialog" aria-modal="true">
      <div className="modal">
        <div className="modalHead">
          <div>
            <h3>Submit Support Ticket</h3>
            <div style={{ marginTop:4, fontSize:12, color:"rgba(255,255,255,.70)" }}>
              Ticket submission is gated by maintenance/support status in production. (Mock behavior included.)
            </div>
          </div>
          <button className="btn" onClick={close}>Close</button>
        </div>
        <div className="modalBody">
          <div className="filters">
            <div className="field">
              <label>Product</label>
              <select value={product} onChange={e => setProduct(e.target.value)}>
                <option value="RedHawk Linux">RedHawk Linux</option>
                <option value="SIMulation Workbench">SIMulation Workbench</option>
              </select>
            </div>
            <div className="field">
              <label>Version</label>
              <select value={version} onChange={e => setVersion(e.target.value)}>
                {versions.length ? (
                  versions.map(v => <option key={v} value={v}>{v}</option>)
                ) : (
                  <option value="—">—</option>
                )}
              </select>
            </div>
            <div className="field">
              <label>Severity</label>
              <select value={severity} onChange={e => setSeverity(e.target.value)}>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>
          </div>

          <div style={{ height:12 }} />

          <div className="filters">
            <div className="field" style={{ flex:1, minWidth:260 }}>
              <label>Environment (OS / HW / Target)</label>
              <input
                value={env}
                onChange={e => setEnv(e.target.value)}
                placeholder="e.g., RedHawk 8.4 on iHawk 9000, kernel 5.x, Xenomai…"
              />
            </div>
          </div>

          <div style={{ height:12 }} />

          <div className="field">
            <label>Description</label>
            <textarea
              value={desc}
              onChange={e => setDesc(e.target.value)}
              placeholder="Describe the issue, expected behavior, observed behavior, steps to reproduce…"
            />
          </div>

          <div style={{ height:12 }} />

          {expired && (
            <div className="empty">
              <strong>Support coverage is expired for this product.</strong><br/>
              In production, you can still submit a ticket, but it will be routed as "billing / renewal required" or "best effort" depending on policy.
            </div>
          )}
        </div>
        <div className="modalFoot">
          <button className="btn" onClick={() => window.alert("Draft saved (mock).")}>Save Draft</button>
          <button className="btn primary" onClick={submit}>Submit Ticket</button>
        </div>
      </div>
    </div>
  );
}

interface ToastProps {
  message: string | null;
}

export function Toast({ message }: ToastProps): React.ReactElement | null {
  if (!message) return null;
  return (
    <div
      style={{
        position:"fixed",
        left:"50%",
        bottom:18,
        transform:"translateX(-50%)",
        padding:"10px 12px",
        borderRadius:14,
        border:"1px solid rgba(255,255,255,.14)",
        background:"rgba(16,31,61,.92)",
        boxShadow:"0 14px 40px rgba(0,0,0,.45)",
        color:"rgba(255,255,255,.92)",
        fontSize:13,
        zIndex:60,
      }}
    >
      {message}
    </div>
  );
}
